import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CircleIcon from "@mui/icons-material/Circle";
import ContactSupportOutlinedIcon from "@mui/icons-material/ContactSupportOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Container,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// --- HOOKS ---
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { useMetaTags } from "../../../hooks/useMetaTags";

// --- CUSTOM SCROLL SPY HOOK ---
// This handles updating the active sidebar link while scrolling
const useScrollSpy = (ids: string[], offset = 150) => {
  const [activeId, setActiveId] = useState<string>(ids[0]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + offset;
      const currentSection = ids.reduce((acc, id) => {
        const element = document.getElementById(id);
        if (element && element.offsetTop <= scrollPosition) {
          return id;
        }
        return acc;
      }, ids[0]);
      setActiveId(currentSection);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [ids, offset]);
  return activeId;
};

// --- STYLED HELPERS ---
const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="h5"
    gutterBottom
    sx={{
      fontWeight: 700,
      color: "text.primary",
      mb: 2,
      fontSize: { xs: "1.2rem", md: "1.4rem" },
    }}
  >
    {children}
  </Typography>
);

const SectionText = ({
  children,
  sx,
}: {
  children: React.ReactNode;
  sx?: any;
}) => (
  <Typography
    variant="body1"
    paragraph
    sx={{
      fontSize: { xs: "0.875rem", sm: "1rem" },
      lineHeight: 1.7,
      color: "text.secondary",
      ...sx,
    }}
  >
    {children}
  </Typography>
);

const ReturnRefundPolicy: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isMobile } = useBreakpoint();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // --- DATA DEFINITIONS (Memoized for Translations) ---

  const SECTIONS = useMemo(
    () => [
      {
        id: "pre-export-protection",
        title: t("returnPolicyPage.sections.preExport"),
      },
      {
        id: "post-shipment-policy",
        title: t("returnPolicyPage.sections.postShipment"),
      },
      {
        id: "package-inspection",
        title: t("returnPolicyPage.sections.inspection"),
      },
      {
        id: "non-returnable-items",
        title: t("returnPolicyPage.sections.nonReturnable"),
      },
      {
        id: "need-help",
        title: t("returnPolicyPage.sections.help"),
      },
    ],
    [t]
  );

  const PRE_EXPORT_LIST = useMemo(
    () => [
      t("returnPolicyPage.preExport.list.item1"),
      t("returnPolicyPage.preExport.list.item2"),
      t("returnPolicyPage.preExport.list.item3"),
      t("returnPolicyPage.preExport.list.item4"),
    ],
    [t]
  );

  const POST_SHIPMENT_STATUSES = useMemo(
    () => [
      t("returnPolicyPage.postShipment.status.departed"),
      t("returnPolicyPage.postShipment.status.transit"),
      t("returnPolicyPage.postShipment.status.customs"),
      t("returnPolicyPage.postShipment.status.arrived"),
    ],
    [t]
  );

  const INSPECTION_CHECKLIST = useMemo(
    () => [
      t("returnPolicyPage.inspection.checklist.packaging"),
      t("returnPolicyPage.inspection.checklist.leaks"),
      t("returnPolicyPage.inspection.checklist.sealed"),
    ],
    [t]
  );

  const NON_RETURNABLE_TAGS = useMemo(
    () => [
      t("returnPolicyPage.nonReturnable.tags.perishables"),
      t("returnPolicyPage.nonReturnable.tags.hygiene"),
      t("returnPolicyPage.nonReturnable.tags.consumables"),
      t("returnPolicyPage.nonReturnable.tags.finalSale"),
    ],
    [t]
  );

  const sectionIds = useMemo(() => SECTIONS.map((s) => s.id), [SECTIONS]);
  const activeSection = useScrollSpy(sectionIds);

  useMetaTags({
    title: t("returnPolicyPage.meta.title"),
    description: t("returnPolicyPage.meta.description"),
    keywords: t("returnPolicyPage.meta.keywords"),
  });

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 140;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <Box sx={{ minHeight: "50vh", py: { xs: 3, md: 5 } }}>
      <Container maxWidth="xl" disableGutters sx={{ px: 2 }}>
        {/* --- MOBILE NAV --- */}
        {isMobile && (
          <Paper
            variant="outlined"
            sx={{ mb: 4, p: 2, bgcolor: "grey.50" }}
            className="notranslate"
            translate="no"
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              sx={{ cursor: "pointer" }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                {t("returnPolicyPage.onThisPage")}
              </Typography>
              <IconButton size="small">
                <ExpandMoreIcon
                  sx={{
                    transform: mobileMenuOpen
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              </IconButton>
            </Box>

            <Box sx={{ display: mobileMenuOpen ? "block" : "none", mt: 2 }}>
              {SECTIONS.map((section, idx) => (
                <Link
                  key={section.id}
                  component="button"
                  onClick={() => scrollToSection(section.id)}
                  sx={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    py: 1,
                    textDecoration: "none",
                    color:
                      activeSection === section.id
                        ? theme.palette.warning.main
                        : theme.palette.text.secondary,
                    fontWeight: activeSection === section.id ? 700 : 400,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    fontSize: "0.9rem",
                  }}
                >
                  {idx + 1}. {section.title}
                </Link>
              ))}
            </Box>
          </Paper>
        )}

        <Box sx={{ display: "flex", gap: 6 }}>
          {/* --- DESKTOP SIDEBAR --- */}
          {isMdUp && (
            <Box
              sx={{ width: 260, flexShrink: 0 }}
              className="notranslate"
              translate="no"
            >
              <Box sx={{ position: "sticky", top: 140 }}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    color: "text.secondary",
                    mb: 2,
                  }}
                >
                  {t("returnPolicyPage.onThisPage")}
                </Typography>
                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                  {SECTIONS.map((section) => {
                    const isActive = activeSection === section.id;
                    return (
                      <Box component="li" key={section.id} sx={{ mb: 0 }}>
                        <Link
                          onClick={() => scrollToSection(section.id)}
                          sx={{
                            display: "block",
                            cursor: "pointer",
                            textDecoration: "none",
                            fontSize: "0.875rem",
                            py: 0.75,
                            pl: 2,
                            borderLeft: "3px solid",
                            borderColor: isActive
                              ? "warning.main"
                              : "transparent",
                            color: isActive ? "text.primary" : "text.secondary",
                            fontWeight: isActive ? 600 : 400,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              color: "warning.main",
                              borderColor: "warning.light",
                              bgcolor: "grey.50",
                            },
                          }}
                        >
                          {section.title}
                        </Link>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          )}

          {/* --- MAIN CONTENT AREA --- */}
          <Box
            sx={{ flex: 1, maxWidth: { md: "800px" } }}
            className="notranslate"
            translate="no"
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "text.primary",
                mb: 2,
                letterSpacing: "-0.5px",
              }}
            >
              {t("returnPolicyPage.title")}
            </Typography>

            <SectionText>
              <span
                dangerouslySetInnerHTML={{
                  __html: t("returnPolicyPage.intro"),
                }}
              />
            </SectionText>
            <SectionText sx={{ fontStyle: "italic", mb: 5 }}>
              {t("returnPolicyPage.operatedBy")}
            </SectionText>

            {/* 1. PRE-EXPORT PROTECTION */}
            <Box
              id="pre-export-protection"
              sx={{ scrollMarginTop: "140px", mb: 6 }}
            >
              <SectionTitle>
                {t("returnPolicyPage.preExport.title")}
              </SectionTitle>
              <SectionText>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("returnPolicyPage.preExport.subtitle"),
                  }}
                />
              </SectionText>

              <List dense>
                {PRE_EXPORT_LIST.map((item, i) => (
                  <ListItem key={i} alignItems="flex-start">
                    <ListItemIcon sx={{ minWidth: 32, mt: 1 }}>
                      <CheckCircleIcon
                        sx={{ fontSize: 18, color: "success.main" }}
                      />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>

              <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>{t("returnPolicyPage.noteLabel")}:</strong>{" "}
                  {t("returnPolicyPage.preExport.note")}
                </Typography>
              </Box>
            </Box>

            {/* 2. NO RETURNS AFTER SHIPMENT */}
            <Box
              id="post-shipment-policy"
              sx={{ scrollMarginTop: "140px", mb: 6 }}
            >
              <SectionTitle>
                {t("returnPolicyPage.postShipment.title")}
              </SectionTitle>

              {/* Warning Box */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "flex-start",
                  p: 2,
                  bgcolor: "#FFF8DC",
                  borderLeft: "4px solid #FFD700",
                  borderRadius: 1,
                  mb: 3,
                }}
              >
                <WarningAmberIcon sx={{ color: "#DAA520", mt: 0.5 }} />
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  {t("returnPolicyPage.postShipment.warning")}
                </Typography>
              </Box>

              <SectionText>
                {t("returnPolicyPage.postShipment.appliesTo")}
              </SectionText>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                  mb: 3,
                }}
              >
                {POST_SHIPMENT_STATUSES.map((item) => (
                  <Paper
                    key={item}
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <AssignmentReturnOutlinedIcon color="disabled" />
                    <Typography variant="body2" fontWeight={500}>
                      {item}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              <SectionText>
                {t("returnPolicyPage.postShipment.reason")}
              </SectionText>
            </Box>

            {/* 3. PACKAGE INSPECTION */}
            <Box
              id="package-inspection"
              sx={{ scrollMarginTop: "140px", mb: 6 }}
            >
              <SectionTitle>
                {t("returnPolicyPage.inspection.title")}
              </SectionTitle>

              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Inventory2OutlinedIcon color="primary" sx={{ fontSize: 32 }} />
                <SectionText sx={{ fontWeight: 600, color: "text.primary" }}>
                  {t("returnPolicyPage.inspection.instruction")}
                </SectionText>
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                {t("returnPolicyPage.inspection.checkLabel")}
              </Typography>
              <List dense sx={{ mb: 2 }}>
                {INSPECTION_CHECKLIST.map((item) => (
                  <ListItem key={item}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CircleIcon sx={{ width: 6, color: "text.primary" }} />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>

              <Paper
                variant="outlined"
                sx={{ p: 3, bgcolor: "error.50", borderColor: "error.200" }}
              >
                <Typography
                  variant="subtitle1"
                  color="error.main"
                  fontWeight={700}
                  gutterBottom
                >
                  {t("returnPolicyPage.inspection.reportTitle")}
                </Typography>
                <SectionText sx={{ mb: 1 }}>
                  {t("returnPolicyPage.inspection.ifDamage")}
                </SectionText>
                <List dense>
                  <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CircleIcon sx={{ width: 6, color: "error.main" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("returnPolicyPage.inspection.action1")}
                    />
                  </ListItem>
                  <ListItem disablePadding sx={{ mt: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CircleIcon sx={{ width: 6, color: "error.main" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("returnPolicyPage.inspection.action2")}
                    />
                  </ListItem>
                </List>
                <Typography
                  variant="body2"
                  sx={{ mt: 2, fontWeight: 600, color: "error.dark" }}
                >
                  {t("returnPolicyPage.inspection.disclaimer")}
                </Typography>
              </Paper>
            </Box>

            {/* 4. NON-RETURNABLE ITEMS */}
            <Box
              id="non-returnable-items"
              sx={{ scrollMarginTop: "140px", mb: 6 }}
            >
              <SectionTitle>
                {t("returnPolicyPage.nonReturnable.title")}
              </SectionTitle>
              <SectionText>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("returnPolicyPage.nonReturnable.intro"),
                  }}
                />
              </SectionText>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                {NON_RETURNABLE_TAGS.map((tag) => (
                  <Box
                    key={tag}
                    sx={{
                      px: 2,
                      py: 1,
                      bgcolor: "grey.200",
                      borderRadius: 4,
                      fontSize: "0.875rem",
                      fontWeight: 500,
                    }}
                  >
                    {tag}
                  </Box>
                ))}
              </Box>

              <SectionText>
                {t("returnPolicyPage.nonReturnable.policyRef")}
              </SectionText>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Link
                  href="https://www.amazon.com/gp/help/customer/display.html?nodeId=GKM69DUUYKQWKWX7"
                  target="_blank"
                  rel="noopener"
                >
                  {t("returnPolicyPage.nonReturnable.links.amazon")}
                </Link>
                <Link
                  href="https://www.ebay.com/help/buying/returns-refunds/return-item-refund?id=4041"
                  target="_blank"
                  rel="noopener"
                >
                  {t("returnPolicyPage.nonReturnable.links.ebay")}
                </Link>
                <Link
                  href="https://www.walmart.com/help/article/walmart-standard-return-policy/81611c0989354e38947e4c2df232630a"
                  target="_blank"
                  rel="noopener"
                >
                  {t("returnPolicyPage.nonReturnable.links.walmart")}
                </Link>
              </Box>
            </Box>

            {/* 5. NEED HELP */}
            <Box id="need-help" sx={{ scrollMarginTop: "140px", mb: 4 }}>
              <SectionTitle>{t("returnPolicyPage.help.title")}</SectionTitle>

              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
                >
                  <ContactSupportOutlinedIcon
                    color="primary"
                    fontSize="large"
                  />
                  <Typography variant="h6" fontWeight={700}>
                    {t("returnPolicyPage.help.subtitle")}
                  </Typography>
                </Box>

                <Box sx={{ display: "grid", gap: 3 }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {t("returnPolicyPage.help.situation1.title")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("returnPolicyPage.help.situation1.text")}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {t("returnPolicyPage.help.situation2.title")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("returnPolicyPage.help.situation2.text")}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {t("returnPolicyPage.help.situation3.title")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <span
                        dangerouslySetInnerHTML={{
                          __html: t("returnPolicyPage.help.situation3.text"),
                        }}
                      />
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    mt: 3,
                    pt: 3,
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body1" fontWeight={600}>
                    {t("returnPolicyPage.help.emailLabel")}{" "}
                    <Link href="mailto:support@us2tjk.com">
                      support@us2tjk.com
                    </Link>
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {t("returnPolicyPage.help.requiredInfo")}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ReturnRefundPolicy;
